<template>
  <transition @enter="enter" @leave="leave">
    <div class="About">
      <div class="About__close"
      @click="close"
      :style="{ backgroundImage: 'url(' + require('./img/close.png') + ')'}" />

      <div class="About__overflow">
        <div class="About__bg" :style="{ backgroundImage: 'url(' + require('./img/bg.jpg') + ')'}">
          <div class="About__quote is-1" v-html="trans('quote_1')" />
          <div class="About__container">
            <div class="About__content">
              <div class="About__quote is-2" v-html="trans('quote_2')" />
              <div v-html="trans('text')" />
              <div class="About__credit" v-html="trans('credit')" />
            </div>
            <div v-if="showPress" class="About__press">
              <a :href="trans('link')" target="_blank" class="About__btn">
                <div class="About__btn-text">
                  {{ trans('btn') }}
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

    </div>
  </transition>
</template>

<script>
import Settings from '@/config/Settings'
export default {
  name: 'About',
  translations: {
    fr: {
      quote_1: 'Stop ! Si vous faites un<br> pas de plus, vous serez<br> <span>arrêtés.</span>',
      quote_2: 'Je sais, je suis <span>désolé.</span><br> Vous devez nous aider ;<br> nous <span>entrons.</span>',
      credit: 'Michel Huneault<br> Montréal, mars 2018',
      btn: 'Crédits et dossier de presse',
      link: 'http://espacemedia.onf.ca/epk/roxham/',
      text: `
        <p>
        Le chemin Roxham est le point d’entrée irrégulière le plus emprunté par les demandeurs d’asile traversant des États-Unis au Canada. Sur ce fragment de frontière autrement tranquille et bucolique, ces arrivées grimpent en flèche au cours de 2017.
        </p>
        <p>
        J’y documente plus de 180 tentatives de passages, sur 16 jours de travail, entre février et août de la même année. Les individus que j’y vois proviennent de plus d’une vingtaine de pays : Érythrée, Soudan, Syrie, Pakistan, Colombie, Turquie, Yémen, Guatemala, Haïti, El Salvador, Tchad, Philippines, Nigéria, Burundi et plusieurs autres. Roxham est un véritable microcosme des crises et conflits du moment. Un mouvement migratoire planétaire nous y rejoint, ici attisé ponctuellement par les politiques de l’administration Trump.
        </p>
        <p>
        À mon premier jour à Roxham, une jeune femme du Nigéria, enceinte, approche avec ses valises. Quand les agents de la Gendarmerie royale du Canada (GRC) l’avertissent de ne pas traverser la frontière, elle fige les deux pieds enfoncés au milieu du fossé enneigé. Elle hésite et pleure, les suppliant de la laisser entrer au Canada. Les minutes passent, puis un agent américain arrive de l’autre côté de la frontière. Il l’embarque, elle disparaît. C’est la première tentative de passage que je vois, et ça m’ébranle.
        </p>
        <p>
        Dès ce premier jour, je me demande comment documenter ce moment important sans mettre à risque les demandeurs d’asile, déjà vulnérables. Je décide de recouvrir leurs silhouettes de tissus que j’ai photographiés ailleurs et avant, en 2015, pendant la crise migratoire en Europe : des photos de couvertures captées en Hongrie, de vêtements propres offerts aux migrants en Autriche ou encore des détails de tentes plantées pour eux en Allemagne. Ces tissus protègent leur identité et rappellent que Roxham fait partie de la même grande histoire.
        </p>
        <p>
        L’étroit chemin Roxham devient rapidement symbolique. Ce moment où une personne choisit de changer sa vie en franchissant une ligne invisible en est un intensément intime. Mais c’est aussi un moment foncièrement public, par les débats politiques et légaux qu’il suscite. Ces personnes ne sont pourtant pas « illégales », ni même leur passage à la frontière : en 1951, le Canada a signé une convention pour accueillir toute personne qui se présente aux autorités en tant que demandeur d’asile, peu importe le lieu de son arrivée. Le passage est donc irrégulier, mais pas illégal. L’individu est, lui, un demandeur d’asile tout à fait légal.
        </p>
        <p>
        Reste que durant ce court instant, cette responsabilité internationale d’accueillir les demandeurs d’asile confronte le devoir de protéger un territoire national. La confusion règne et la tension est palpable. La langue est une barrière en soi; les émotions fusent.
        </p>
        <p>
        À Roxham, debout sur la frontière, un monde en mouvement vient à nous.
         </p>
      `
    },
    en: {
      quote_1: 'Stop! If you walk<br> further, you’ll be<br> <span>arrested.</span>',
      quote_2: 'I know; I am really <span>sorry</span>.<br> You have to help us; we<br> are <span>entering.<span>',
      credit: 'Michel Huneault<br>Montreal, March 2018',
      btn: 'Credits and Press Kit',
      link: 'http://mediaspace.nfb.ca/epk/roxham/',
      text: `
        <p>
        Roxham Road is the location with the largest number of irregular border crossings by asylum seekers moving from the United States to Canada. On this otherwise calm rural section of the border, the number of arrivals climbed sharply throughout 2017.
        </p>
        <p>
        There I documented 180 attempted border crossings over 16 days between February and August 2017. The people I saw had come from more than 20 countries: Eritrea, Sudan, Syria, Pakistan, Colombia, Turkey, Yemen, Guatemala, Haiti, El Salvador, Chad, the Philippines, Nigeria, Burundi, and several others. Roxham is a true microcosm of today’s crises and conflicts. A worldwide migratory movement comes toward us there, periodically prompted by the politics of the Trump administration.
        </p>
        <p>
        On my first day at Roxham, a young, pregnant Nigerian woman approached the border with her suitcases. When the RCMP officers warned her not to cross the border, she froze with her feet sinking into the snow-covered ditch. She hesitated, crying. She begged to be allowed to enter Canada. Minutes passed, and then an American officer arrived on the other side of the border. He picked her up, and she disappeared. It was my first time seeing someone attempt to cross the border, and it shook me.
        </p>
        <p>
        From that first day, I wondered how to document this historical moment without putting the vulnerable asylum seekers further at risk. I decided to mask their silhouettes using fabric I photographed in other places during the 2015 European migrant crisis—images of blankets in Hungary, clean clothing offered to migrants in Austria, or details of tents set up for migrants in Germany. Using this fabric protected their identity and reminded me that this too was part of the same story.
        </p>
        <p>
        Roxham Road is quickly becoming symbolic. This intensely personal moment—when people choose to change their life by crossing an invisible line—is also, essentially, a public moment with political and legal debates surrounding it. Yet, these people aren’t “illegal,” and neither is their border crossing. In 1951, Canada signed a convention to welcome all people who present themselves to authorities seeking asylum, no matter where they arrive. Thus, this crossing is irregular but not illegal, and these individuals are asylum seekers—completely legal.
        </p>
        <p>
        Nevertheless, during this brief moment, the international responsibility of welcoming asylum seekers clashes with the duty to protect a national territory. Confusion reigns, and the tension is palpable.
        Language is a barrier in itself; emotions run high.
        </p>
        <p>
        At Roxham, standing on the edge of the border, a changing world comes toward us.
        </p>
      `
    },
  },
  created() {
    this.showPress = !Settings.localAssets
    this.$store.commit('app/SET_PLAYING', false)
  },
  beforeDestroy() {
    this.$store.commit('app/SET_PLAYING', true)
  },
  methods: {
    close() {
      this.$store.commit('app/SET_ABOUT', false)
    },
    enter(el, done) {
      TweenMax.from(this.$el, 1, { alpha: 0, onComplete: done })
    },
    leave(el, done) {
      TweenMax.to(this.$el, 1, { alpha: 0, onComplete: done })
    },
  }
}
</script>

<style lang="stylus">
@import '~@/config/Settings'
@import '~@/commons/stylus/Media-queries'

.About
  position fixed
  z-index 98
  top 0px
  left 0px
  right 0px
  bottom 0px
  font-family Inknut
  color white
  background #141413


  &__overflow
    position absolute
    top 0px
    left 0px
    right 0px
    bottom 0px
    overflow auto
    -webkit-overflow-scrolling touch

  &__bg
    background-size 100% auto
    background-repeat no-repeat

  &__close
    position absolute
    left 15px
    top 60px
    background-size cover
    width 40px
    height 40px
    cursor pointer
    transform translate3d(0,0,0)
    z-index 9
    +max-screen(phone)
      right 15px
      left auto

  &__container
    lost-center 980px
    padding 250px 0 100px
    +max-screen(phone)
      padding-top 250px

  &__quote
    text-transform uppercase
    position relative
    font-size 35px
    line-height 60px
    opacity .3
    +max-screen(phone)
      font-size 16px
      line-height 35px
    &:before
      content '—'
      position absolute
      left -45px
      top 0px
      +max-screen(phone)
        left -25px
    span
      position relative
      display inline-block
      z-index 1
      &:before
        content ''
        position absolute
        height 55px
        top 5px
        left -10px
        right -10px
        background #450000
        z-index -1
        +max-screen(phone)
          height 35px
          left -5px
          right -5px

    &.is-1
      position absolute
      right 30px
      top 80px
      text-align right
      +max-screen(phone)
        top 110px
        right 15px

    &.is-2
      margin-bottom 30px
      +max-screen(phone)
        margin-left 30px
        margin-bottom 20px

  &__content
    lost-column 8/10 2
    font-family Inknut
    font-size 18px
    line-height 30px
    padding 0 15px 0px
    color #c1bebe
    +max-screen(phone)
      lost-column 9/10
      font-size 14px
      line-height 25px

  &__press
    lost-column 2/10 2
    +max-screen(phone)
      lost-column 1/1

  &__btn
    border-radius 80px
    height 160px
    width 160px
    border 2px solid #fff
    margin 0 auto
    font-size 13px
    text-align center
    position relative
    margin-top 250px
    display block
    color #fff
    +max-screen(phone)
      margin-top 50px
    &:hover
    &:focus
      color #fff
    &-text
      position absolute
      top 50%
      left 0px
      right 0px
      padding 0 10px
      transform translateY(-50%)

  &__credit
    text-align right
    margin-top 70px
    font-family Montserrat



</style>



// WEBPACK FOOTER //
// src/components/about/About.vue